import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getConversations,
  getMessages,
  sendMessage,
} from "../../store/slices/chat";
import { IoIosArrowDropleft } from "react-icons/io";


function ConvListSkeleton() {
  return (
    <>
      {[...Array(6)].map((_, i) => (
        <div key={i} className="conv-item" style={{ cursor: "default" }}>
          {/* avatar circle */}
          <div className="conv-ava-wrap">
            <div className="ep-skeleton-circle" />
          </div>

          {/* name , preview lines */}
          <div className="conv-info" style={{ flex: 1 }}>
            <div className="conv-name-row" style={{ marginBottom: 8 }}>
              <div className="ep-skeleton-title" style={{ width: "55%", marginBottom: 0 }} />
              <div className="ep-skeleton-line" style={{ width: 36, height: 12 }} />
            </div>
            <div className="ep-skeleton-line short" />
          </div>
        </div>
      ))}
    </>
  );
}


function MessagesSkeleton() {

  const rows = [
    { sent: false, width: "55%" },
    { sent: true,  width: "45%" },
    { sent: false, width: "65%" },
    { sent: false, width: "40%" },
    { sent: true,  width: "50%" },
    { sent: true,  width: "35%" },
    { sent: false, width: "60%" },
  ];

  return (
    <>
      {rows.map((row, i) => (
        <div key={i} className={`msg-row ${row.sent ? "sent" : ""}`}>
       
          {!row.sent && (
            <div
              className="ep-skeleton-circle"
              style={{ width: 34, height: 34, flexShrink: 0 }}
            />
          )}

         
          <div
            className="ep-skeleton-square"
            style={{
              width: row.width,
              height: 42,
              borderRadius: 16,
              background: undefined, 
            }}
          />
        </div>
      ))}
    </>
  );
}

export default function Messages() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeChatId, setActiveChatId] = useState(1);

  const [showChat, setShowChat] = useState(false);
  const [inputText, setInputText] = useState("");
  const [search, setSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [allConvs, setAllConvs] = useState([]);
  const [messages, setMessages] = useState([]);


  const [loadingConvs, setLoadingConvs] = useState(false);   
  const [loadingMsgs,  setLoadingMsgs]  = useState(false);  


  const user   = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.userId;

  useEffect(() => {
    const loadConversations = async () => {
      try {
        setLoadingConvs(true); 
        const res = await dispatch(getConversations()).unwrap();
        setAllConvs(res?.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingConvs(false); 
      }
    };

    loadConversations();
  }, [dispatch]);

  const handleSelectConv = async (conv) => {
    setActiveChatId(conv._id);
    setShowChat(true);

    try {
      setLoadingMsgs(true); 
      const item = { conversationId: conv._id };
      const res  = await dispatch(getMessages(item)).unwrap();
      setMessages(res?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMsgs(false);
    }
  };

  const activeConv = allConvs.find((c) => c._id === activeChatId);

  const filteredConvs = allConvs.filter((c) =>
    (c?.user?.name || "").toLowerCase().includes(search.toLowerCase()),
  );

  // scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeChatId, allConvs]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || !activeConv) return;

    try {
      // 1️⃣ send message to backend
      const res = await dispatch(
        sendMessage({
          toUserId: activeConv.user._id,
          text,
        }),
      ).unwrap();

      // 2️⃣ update UI instantly
      if (res?.data) {
        setMessages((prev) => [...prev, res.data]);
      }

      // 3️⃣ update conversation last message
      setAllConvs((prev) =>
        prev.map((c) =>
          c._id === activeChatId
            ? {
                ...c,
                lastMessage: text,
                lastMessageAt: new Date().toISOString(),
              }
            : c,
        ),
      );

      setInputText("");
      textareaRef.current?.focus();
    } catch (err) {
      console.error(err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div className="msg-root">
        {/* TOP NAV */}
        <div className="msg-topbar">
          <div className="msg-topbar-title">Messages ✦</div>
          {/* <span className="msg-topbar-badge">
            {allConvs.reduce((a, c) => a + c.unread, 0)} unread
          </span> */}
        </div>

        <div className={`msg-layout${showChat ? " show-chat" : ""}`}>
          {/* ── LEFT PANEL ── */}
          <div className="conv-panel">
            <div className="conv-search-wrap">
              <input
                className="conv-search"
                placeholder="Search conversations…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="conv-list">
           
              {loadingConvs ? (
                <ConvListSkeleton />
              ) : (
                filteredConvs.map((conv) => (
                  <div
                    key={conv.id}
                    className={`conv-item${activeChatId === conv.id ? " active" : ""}`}
                    onClick={() => handleSelectConv(conv)}
                  >
                    <div className="conv-ava-wrap">
                      <img
                        src={conv?.user?.profilePhoto}
                        alt={conv?.user?.name}
                        className="conv-ava"
                      />
                      {conv.online && <div className="conv-online-dot" />}
                    </div>
                    <div className="conv-info">
                      <div className="conv-name-row">
                        <div className="conv-name">{conv.user.name}</div>
                        <div className="conv-time">{conv.lastTime}</div>
                      </div>
                      <div
                        className={`conv-preview${conv.unread > 0 ? " unread" : ""}`}
                      >
                        {conv.lastMsg}
                      </div>
                    </div>
                    {conv.unread > 0 && <div className="conv-unread-dot" />}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── RIGHT CHAT PANEL ── */}
          {activeConv ? (
            <div className="chat-panel">
              {/* chat header */}
              <div className="chat-header">
                <img
                  src={activeConv?.user?.profilePhoto || "/default-user.png"}
                  alt={activeConv.name}
                  className="chat-header-ava"
                />
                <div className="chat-header-info">
                  <div className="chat-header-name">
                    {" "}
                    {activeConv?.user?.name}
                  </div>
                  <div
                    className={`chat-header-status${activeConv.online ? "" : " offline"}`}
                  >
                    {activeConv.online
                      ? "● Online now"
                      : "● Last seen recently"}
                  </div>
                </div>
                <button
                  className="mobile-back-btn"
                  onClick={() => setShowChat(false)}
                >
                  <IoIosArrowDropleft style={{ color: "#8a1232" }} size={20} />
                </button>
              </div>

              {/* mini profile card */}
              {/* <div className="chat-profile-mini">
                <img src={activeConv.user.profilePhoto} alt={activeConv.name} />
                <div className="chat-profile-mini-info">
                  <div className="chat-profile-mini-name">
                    {activeConv.user.name}
                  </div>
                  <div className="chat-profile-mini-meta">
                    {activeConv.user.occupation} · {activeConv.location}
                  </div>
                  <div className="chat-profile-mini-chips">
                    <span className="mini-chip">✓ Verified</span>
                    <span className="mini-chip">Premium</span>
                  </div>
                </div>
                <button
                  className="btn-view-profile"
                  onClick={() => navigate("/user/profile-details")}
                >
                  View Profile →
                </button>
              </div> */}

              {/* messages — show skeleton while loading */}
              <div className="chat-messages">
                {loadingMsgs ? (
                  <MessagesSkeleton />
                ) : (
                  messages.map((msg) => {
                    const senderId = msg?.sender?._id || msg?.sender;
                    const isMe     = String(senderId) === String(userId);

                    return (
                      <div
                        key={msg._id}
                        className={`msg-row ${isMe ? "sent" : ""}`}
                      >
                        {!isMe && (
                          <img
                            src={activeConv?.user?.profilePic}
                            alt=""
                            className="msg-row-ava"
                          />
                        )}

                        <div className={`bubble ${isMe ? "sent" : "received"}`}>
                          {msg.text}
                          <span className="bubble-time">
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour:   "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* input bar */}
              <div className="chat-input-bar">
                <div className="chat-input-row">
                  <textarea
                    ref={textareaRef}
                    className="chat-textarea"
                    placeholder={`Message ${activeConv.name}…`}
                    value={inputText}
                    rows={1}
                    onChange={(e) => {
                      setInputText(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height =
                        Math.min(e.target.scrollHeight, 100) + "px";
                    }}
                    onKeyDown={handleKeyDown}
                  />
                  <div className="chat-input-actions">
                    <button
                      className="send-btn"
                      onClick={handleSend}
                      disabled={!inputText.trim()}
                    >
                      ➤
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="chat-panel">
              <div className="no-chat">
                <div className="no-chat-icon">💌</div>
                <div className="no-chat-title">Select a conversation</div>
                <div className="no-chat-sub">
                  Choose a match to start chatting
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}